import {
  SectionElement,
  defineElement,
  query,
  queryAll,
  wait
} from "./chunk-DNDL4K7L.js";
import {
  __async,
  __decorateClass
} from "./chunk-XS5WIYU5.js";

// src/sections/new-hero/phones-coverflow.ts
var clamp = (value, min, max) => Math.min(max, Math.max(min, value));
var PhonesCoverflow = class {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.mobileQuery = window.matchMedia("(max-width: 1023px)");
    this.active = false;
    this.handleBreakpoint = () => {
      this.active = this.mobileQuery.matches;
      this.wrapper.toggleAttribute("data-coverflow", this.active);
      if (this.active)
        this.requestFrame();
      else
        this.reset();
    };
    // tapping a side phone brings it to the middle
    this.handleClick = (event) => {
      var _a;
      if (!this.active)
        return;
      const item = (_a = event.target) == null ? void 0 : _a.closest("[scrollable-item]");
      if (!item)
        return;
      const rect = item.getBoundingClientRect();
      const box = this.container.getBoundingClientRect();
      const offset = rect.left + rect.width / 2 - (box.left + box.width / 2);
      if (Math.abs(offset) > rect.width * 0.3)
        this.container.scrollBy({ left: offset, behavior: "smooth" });
    };
    this.requestFrame = () => {
      if (this.frameId !== void 0 || !this.active)
        return;
      this.frameId = requestAnimationFrame(this.update);
    };
    this.update = () => {
      this.frameId = void 0;
      const box = this.container.getBoundingClientRect();
      const center = box.left + box.width / 2;
      this.items.forEach((item) => {
        const width = item.offsetWidth;
        const itemCenter = item.offsetLeft - this.container.scrollLeft + box.left + width / 2;
        const d = (itemCenter - center) / width;
        const a = Math.abs(d);
        if (a > 3.5) {
          item.style.opacity = "0";
          return;
        }
        const turn = clamp(d, -1.6, 1.6);
        const pull = Math.sign(d) * Math.min(a, 2) * width * 0.2;
        item.style.transform = `translateX(${(-pull).toFixed(1)}px) translateZ(${(-Math.min(a, 2.5) * width * 0.35).toFixed(1)}px) rotateY(${(-turn * 32).toFixed(2)}deg) scale(${(1.08 - Math.min(a, 1) * 0.08).toFixed(3)})`;
        item.style.opacity = (1 - clamp(a - 1.6, 0, 1) * 0.75).toFixed(3);
        item.style.zIndex = String(100 - Math.round(a * 10));
      });
    };
    const container = wrapper.querySelector("[scrollable-container]");
    if (!container)
      throw new Error("coverflow needs the scrollable mockup view");
    this.container = container;
    this.items = Array.from(container.querySelectorAll("[scrollable-item]"));
    this.observer = new IntersectionObserver(([entry]) => {
      if (!(entry == null ? void 0 : entry.isIntersecting))
        return;
      wrapper.setAttribute("data-coverflow-in", "");
      this.observer.disconnect();
    }, { threshold: 0.2 });
    this.observer.observe(container);
    container.addEventListener("scroll", this.requestFrame, { passive: true });
    container.addEventListener("click", this.handleClick);
    window.addEventListener("resize", this.requestFrame);
    this.mobileQuery.addEventListener("change", this.handleBreakpoint);
    this.handleBreakpoint();
  }
  dispose() {
    if (this.frameId)
      cancelAnimationFrame(this.frameId);
    this.observer.disconnect();
    this.container.removeEventListener("scroll", this.requestFrame);
    this.container.removeEventListener("click", this.handleClick);
    window.removeEventListener("resize", this.requestFrame);
    this.mobileQuery.removeEventListener("change", this.handleBreakpoint);
    this.reset();
  }
  reset() {
    this.wrapper.removeAttribute("data-coverflow");
    this.items.forEach((item) => {
      item.style.removeProperty("transform");
      item.style.removeProperty("opacity");
      item.style.removeProperty("z-index");
    });
  }
};

// src/sections/new-hero/new-hero.ts
var NewHero = class extends SectionElement {
  constructor() {
    super(...arguments);
    this.defaultIntervalMs = 3e3;
    this.rotationIntervalMs = this.defaultIntervalMs;
    this.currentIndex = 0;
    this.isAnimating = false;
  }
  connectedCallback() {
    if (this.rotatingTextWrapper && this.rotatingItems && this.rotatingItems.length > 1) {
      this.rotationIntervalMs = this.getRotationInterval();
      this.rotationIntervalId = window.setInterval(() => {
        void this.showNextText();
      }, this.rotationIntervalMs);
    }
    void this.initPhones3d();
    this.initPhonesCoverflow();
  }
  disconnectedCallback() {
    var _a, _b;
    if (this.rotationIntervalId) {
      window.clearInterval(this.rotationIntervalId);
      this.rotationIntervalId = void 0;
    }
    (_a = this.phones3d) == null ? void 0 : _a.dispose();
    this.phones3d = void 0;
    (_b = this.phonesCoverflow) == null ? void 0 : _b.dispose();
    this.phonesCoverflow = void 0;
  }
  // Opt-in mobile carousel (section setting "3D swipe carousel (mobile)"): CSS 3D transforms on the scrollable mockup view, no three.js.
  // It switches itself on and off at the 1024px breakpoint, so it is always set up when the wrapper exists.
  initPhonesCoverflow() {
    if (!this.phonesCoverflowWrapper || this.phonesCoverflow)
      return;
    try {
      this.phonesCoverflow = new PhonesCoverflow(this.phonesCoverflowWrapper);
    } catch (error) {
      console.warn("new-hero: mobile carousel unavailable", error);
    }
  }
  // Opt-in 3D phones (section setting "3D phones"): three.js is only fetched with motion allowed and WebGL available.
  // The regular gallery starts hidden when 3D phones are on (new-hero.css), so the straight row never
  // flashes before the fan. Whenever the 3D scene can't run, the gallery is shown instead.
  initPhones3d() {
    return __async(this, null, function* () {
      var _a;
      const wrapper = this.phones3dWrapper;
      if (!wrapper || this.phones3d)
        return;
      const showGallery = () => {
        wrapper.setAttribute("data-phones-3d-fallback", "");
      };
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        showGallery();
        return;
      }
      try {
        const { Phones3D, supportsWebGL } = yield import("./phones-3d-4PKP3X6L.js");
        if (!this.isConnected || this.phones3d)
          return;
        if (!supportsWebGL()) {
          showGallery();
          return;
        }
        const phones3d = new Phones3D(wrapper);
        this.phones3d = phones3d;
        yield phones3d.load();
      } catch (error) {
        console.warn("new-hero: 3D phones unavailable, keeping the mockup gallery", error);
        (_a = this.phones3d) == null ? void 0 : _a.dispose();
        this.phones3d = void 0;
        showGallery();
      }
    });
  }
  getRotationInterval() {
    var _a;
    const intervalSeconds = Number((_a = this.rotatingTextWrapper) == null ? void 0 : _a.dataset.rotatingInterval);
    if (Number.isFinite(intervalSeconds) && intervalSeconds > 0) {
      return intervalSeconds * 1e3;
    }
    return this.defaultIntervalMs;
  }
  showNextText() {
    return __async(this, null, function* () {
      if (!this.rotatingItems || !this.rotatingTextWrapper || this.isAnimating)
        return;
      const previous = this.rotatingItems[this.currentIndex];
      this.currentIndex = (this.currentIndex + 1) % this.rotatingItems.length;
      const next = this.rotatingItems[this.currentIndex];
      if (!previous || !next || previous === next)
        return;
      this.isAnimating = true;
      yield this.rotateText(previous, next);
      this.isAnimating = false;
    });
  }
  rotateText(previous, next) {
    return __async(this, null, function* () {
      yield this.animateElement(previous, "out");
      previous.classList.add("invisible");
      next.classList.remove("invisible");
      yield this.animateElement(next, "in");
    });
  }
  waitForAnimationFrame() {
    return __async(this, null, function* () {
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resolve();
          });
        });
      });
    });
  }
  animateElement(element, direction) {
    return __async(this, null, function* () {
      const keyframes = direction === "out" ? [
        { opacity: 1, transform: "translateY(0)" },
        { opacity: 0, transform: "translateY(-0.35em)" }
      ] : [
        { opacity: 0, transform: "translateY(0.35em)" },
        { opacity: 1, transform: "translateY(0)" }
      ];
      const duration = 150;
      const animation = typeof element.animate === "function" ? element.animate(keyframes, { duration, easing: "ease-out", fill: "forwards" }) : void 0;
      yield this.waitForAnimation(animation, duration);
    });
  }
  waitForAnimation(animation, fallbackDuration) {
    return __async(this, null, function* () {
      if (!animation) {
        yield wait(fallbackDuration);
        return;
      }
      if ("finished" in animation) {
        yield animation.finished.catch(() => {
        });
      } else {
        yield wait(fallbackDuration);
      }
    });
  }
};
__decorateClass([
  query("[data-rotating-text]")
], NewHero.prototype, "rotatingTextWrapper", 2);
__decorateClass([
  queryAll("[data-rotating-text-item]")
], NewHero.prototype, "rotatingItems", 2);
__decorateClass([
  query("[data-phones-3d]")
], NewHero.prototype, "phones3dWrapper", 2);
__decorateClass([
  query("[data-phones-coverflow]")
], NewHero.prototype, "phonesCoverflowWrapper", 2);
NewHero = __decorateClass([
  defineElement("new-hero")
], NewHero);
export {
  NewHero
};
